import { ValidationRule } from '../types';

// Predefined Python validation rules
export const getDefaultPythonValidationRules = (): ValidationRule[] => [
  {
    id: 'python_print',
    type: 'pattern',
    condition: 'require_print',
    message: 'You must use the print() function to display output.',
    language: 'python',
    severity: 'error',
    required: true,
  },
  {
    id: 'python_function_def',
    type: 'pattern',
    condition: 'require_function_def',
    message: 'You must define at least one function using def.',
    language: 'python',
    severity: 'error',
    required: true,
  },
  {
    id: 'python_if_statement',
    type: 'pattern',
    condition: 'require_if',
    message: 'You must use conditional statements (if, elif, else).',
    language: 'python',
    severity: 'error',
    required: true,
  },
  {
    id: 'python_while_loop',
    type: 'pattern',
    condition: 'require_while',
    message: 'You must use a while loop.',
    language: 'python',
    severity: 'error',
    required: true,
  },
  {
    id: 'python_for_loop',
    type: 'pattern',
    condition: 'require_for',
    message: 'You must use a for loop.',
    language: 'python',
    severity: 'error',
    required: true,
  },
  {
    id: 'python_random_module',
    type: 'pattern',
    condition: 'require_random_module',
    message: 'You must import the random module.',
    language: 'python',
    severity: 'error',
    required: true,
  },
  {
    id: 'python_randint_function',
    type: 'pattern',
    condition: 'require_randint_function',
    message: 'You must use the random.randint() function.',
    language: 'python',
    severity: 'error',
    required: true,
  },
  {
    id: 'python_arithmetic_ops',
    type: 'pattern',
    condition: 'require_arithmetic_ops',
    message: 'You must use arithmetic operators (+, -, *, /).',
    language: 'python',
    severity: 'error',
    required: true,
  },
  {
    id: 'python_list',
    type: 'pattern',
    condition: 'require_list',
    message: 'You must use a list.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_dict',
    type: 'pattern',
    condition: 'require_dict',
    message: 'You must use a dictionary.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_import_math',
    type: 'pattern',
    condition: 'require_import_math',
    message: 'You must import the math module.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_try_except',
    type: 'pattern',
    condition: 'require_try_except',
    message: 'You must use try-except for error handling.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_class_def',
    type: 'pattern',
    condition: 'require_class_def',
    message: 'You must define a class.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_lambda',
    type: 'pattern',
    condition: 'require_lambda',
    message: 'You must use a lambda function.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_list_comprehension',
    type: 'pattern',
    condition: 'require_list_comprehension',
    message: 'You must use list comprehension.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_string_formatting',
    type: 'pattern',
    condition: 'require_string_formatting',
    message: 'You must use string formatting.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_file_operations',
    type: 'pattern',
    condition: 'require_file_operations',
    message: 'You must perform file operations.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
  {
    id: 'python_os_module',
    type: 'pattern',
    condition: 'require_os_module',
    message: 'You must import the os module.',
    language: 'python',
    severity: 'warning',
    required: false,
  },
];

// Predefined JavaScript validation rules
export const getDefaultJavaScriptValidationRules = (): ValidationRule[] => [
  {
    id: 'javascript_console_log',
    type: 'pattern',
    condition: 'require_console_log',
    message: 'You must use console.log() to display output.',
    language: 'javascript',
    severity: 'error',
    required: true,
  },
  {
    id: 'javascript_function_def',
    type: 'pattern',
    condition: 'require_function_def',
    message: 'You must define at least one function.',
    language: 'javascript',
    severity: 'error',
    required: true,
  },
  {
    id: 'javascript_if_statement',
    type: 'pattern',
    condition: 'require_if',
    message: 'You must use conditional statements (if, else if, else).',
    language: 'javascript',
    severity: 'error',
    required: true,
  },
  {
    id: 'javascript_while_loop',
    type: 'pattern',
    condition: 'require_while',
    message: 'You must use a while loop.',
    language: 'javascript',
    severity: 'error',
    required: true,
  },
  {
    id: 'javascript_for_loop',
    type: 'pattern',
    condition: 'require_for',
    message: 'You must use a for loop.',
    language: 'javascript',
    severity: 'error',
    required: true,
  },
  {
    id: 'javascript_math_module',
    type: 'pattern',
    condition: 'require_math_module',
    message: 'You must use the Math object.',
    language: 'javascript',
    severity: 'error',
    required: true,
  },
  {
    id: 'javascript_math_random',
    type: 'pattern',
    condition: 'require_math_random',
    message: 'You must use Math.random() function.',
    language: 'javascript',
    severity: 'error',
    required: true,
  },
  {
    id: 'javascript_arithmetic_ops',
    type: 'pattern',
    condition: 'require_arithmetic_ops',
    message: 'You must use arithmetic operators (+, -, *, /).',
    language: 'javascript',
    severity: 'error',
    required: true,
  },
  {
    id: 'javascript_arrow_function',
    type: 'pattern',
    condition: 'require_arrow_function',
    message: 'You must use an arrow function.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_array',
    type: 'pattern',
    condition: 'require_array',
    message: 'You must use an array.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_object',
    type: 'pattern',
    condition: 'require_object',
    message: 'You must use an object.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_try_catch',
    type: 'pattern',
    condition: 'require_try_catch',
    message: 'You must use try-catch for error handling.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_class_def',
    type: 'pattern',
    condition: 'require_class_def',
    message: 'You must define a class.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_async_function',
    type: 'pattern',
    condition: 'require_async_function',
    message: 'You must use async/await.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_promise',
    type: 'pattern',
    condition: 'require_promise',
    message: 'You must use Promises.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_fetch',
    type: 'pattern',
    condition: 'require_fetch',
    message: 'You must use the fetch API.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_dom_manipulation',
    type: 'pattern',
    condition: 'require_dom_manipulation',
    message: 'You must manipulate the DOM.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_json',
    type: 'pattern',
    condition: 'require_json',
    message: 'You must use JSON methods.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_localStorage',
    type: 'pattern',
    condition: 'require_localStorage',
    message: 'You must use localStorage.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_setTimeout',
    type: 'pattern',
    condition: 'require_setTimeout',
    message: 'You must use setTimeout.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
  {
    id: 'javascript_setInterval',
    type: 'pattern',
    condition: 'require_setInterval',
    message: 'You must use setInterval.',
    language: 'javascript',
    severity: 'warning',
    required: false,
  },
];

// Get default validation rules for a specific language
export const getDefaultValidationRules = (language: 'python' | 'javascript'): ValidationRule[] => {
  return language === 'python' ? getDefaultPythonValidationRules() : getDefaultJavaScriptValidationRules();
};

// Create a validation rule from JSON (for imported configurations)
export const createValidationRuleFromJSON = (jsonRule: any): ValidationRule => {
  return {
    id: jsonRule.id || `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: jsonRule.type || 'pattern',
    condition: jsonRule.condition || '',
    message: jsonRule.message || 'Validation rule',
    language: jsonRule.language || 'python',
    severity: jsonRule.severity || 'error',
    required: jsonRule.required !== undefined ? jsonRule.required : true,
  };
};

// Validate and sanitize imported validation rules
export const validateAndSanitizeRules = (rules: any[]): ValidationRule[] => {
  if (!Array.isArray(rules)) {
    console.warn('Validation rules must be an array');
    return [];
  }

  return rules
    .filter(rule => rule && typeof rule === 'object')
    .map(rule => createValidationRuleFromJSON(rule))
    .filter(rule => {
      // Validate required fields
      if (!rule.condition || !rule.message) {
        console.warn('Invalid validation rule: missing condition or message', rule);
        return false;
      }
      
      // Validate type
      if (!['syntax', 'function', 'import', 'output', 'pattern'].includes(rule.type)) {
        console.warn('Invalid validation rule type:', rule.type);
        return false;
      }
      
      // Validate language
      if (!['python', 'javascript'].includes(rule.language)) {
        console.warn('Invalid validation rule language:', rule.language);
        return false;
      }
      
      // Validate severity
      if (!['error', 'warning'].includes(rule.severity)) {
        console.warn('Invalid validation rule severity:', rule.severity);
        return false;
      }
      
      return true;
    });
};

// Export validation rules as JSON (for configuration export)
export const exportValidationRulesAsJSON = (rules: ValidationRule[]): any[] => {
  return rules.map(rule => ({
    id: rule.id,
    type: rule.type,
    condition: rule.condition,
    message: rule.message,
    language: rule.language,
    severity: rule.severity,
    required: rule.required,
  }));
};
